/**
 * @file apps/web/src/pages/portal/PortalApprovalsPage.tsx
 * @description Client portal approvals — shows pending and completed approval workflows.
 * Client users can approve or reject steps where they are the designated approver.
 * Route: /portal/approvals
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { notifyProjectOverviewChanged } from '../../lib/projectOverviewEvents.js';
import {
  Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp,
  Shield, FileText, ExternalLink,
} from 'lucide-react';
import { usePortalAuth } from '../../lib/portalAuth.js';
import { approvalsApi, documentsApi } from '../../lib/api.js';
import {
  approvalTitle, groupApprovalsForUser, findApprovalDocument, portalDocumentPath,
  isWaitingForUser,
} from '../../lib/approvalCards.js';
import { ApprovalStepper, type ApprovalStep } from '../../components/ApprovalStepper.js';
import { AuditTrail, type AuditEvent } from '../../components/AuditTrail.js';
import { Badge } from '../../components/Badge.js';
import type { WorkflowType } from '../../components/RoutingBuilder.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Approval {
  id:            string;
  status:        string;
  workflow_type: WorkflowType;
  entity_type:   string;
  entity_id:     string;
  title?:        string | null;
  document_id?:  string | null;
  created_at:    string;
  steps?:        ApprovalStep[];
}

// The slice of a document the approval card needs (GET /documents).
interface DocumentSummary {
  id:     string;
  title:  string;
  status: string;
}

// ---------------------------------------------------------------------------
// Approval item
// ---------------------------------------------------------------------------

function ApprovalItem({
  approval,
  currentUserId,
  document,
  onDecide,
}: {
  approval:      Approval;
  currentUserId: string;
  document?:     DocumentSummary;
  onDecide:      (approvalId: string, stepId: string, decision: 'approved' | 'rejected') => Promise<void>;
}) {
  const [expanded, setExpanded]   = useState(false);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const { devHeaders } = usePortalAuth();

  const handleToggle = () => {
    setExpanded((v) => {
      if (!v && auditEvents.length === 0) {
        setAuditLoading(true);
        approvalsApi.getAudit(approval.id, devHeaders)
          .then((data) => setAuditEvents(data as AuditEvent[]))
          .catch(() => setAuditEvents([]))
          .finally(() => setAuditLoading(false));
      }
      return !v;
    });
  };

  const statusIcon = {
    pending:     <Clock size={14} className="text-warning" />,
    in_progress: <Clock size={14} className="text-blue" />,
    approved:  <CheckCircle size={14} className="text-success" />,
    rejected:  <XCircle size={14} className="text-danger" />,
    cancelled: <XCircle size={14} className="text-muted" />,
  }[approval.status] ?? <Clock size={14} className="text-muted" />;

  // Does this user have an active, undecided step on a still-open approval?
  const myPendingStep = isWaitingForUser(approval, currentUserId);

  return (
    <div className={[
      'card overflow-hidden',
      myPendingStep ? 'ring-2 ring-blue/20' : '',
    ].join(' ')}>
      {myPendingStep && (
        <div
          className="px-4 py-1.5 text-[11px] font-mono font-medium flex items-center gap-1.5"
          style={{ backgroundColor: 'var(--sky)', color: 'var(--blue)' }}
        >
          <Shield size={11} />
          Your approval is required
        </div>
      )}

      <button
        onClick={handleToggle}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-sky/20 transition-colors"
      >
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {statusIcon}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink truncate">
              {approvalTitle(approval)}
            </p>
            <p className="text-[11px] font-mono text-muted mt-0.5">
              {approval.workflow_type} · {new Date(approval.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={approval.status as never} size="xs" />
          {expanded
            ? <ChevronUp size={14} className="text-muted" />
            : <ChevronDown size={14} className="text-muted" />
          }
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-4 space-y-4">
          {document && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-sky/50">
              <div className="flex items-center gap-2.5 min-w-0">
                <FileText size={16} className="text-blue shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-mono text-muted uppercase tracking-wide">Document to review</p>
                  <p className="text-sm font-medium text-ink truncate">{document.title}</p>
                </div>
              </div>
              <Link
                to={portalDocumentPath(document.id)}
                className="btn-secondary text-xs shrink-0 inline-flex items-center gap-1.5"
              >
                View document <ExternalLink size={12} />
              </Link>
            </div>
          )}
          <ApprovalStepper
            steps={approval.steps ?? []}
            workflowType={approval.workflow_type}
            currentUserId={currentUserId}
            onDecide={(stepId, decision) => onDecide(approval.id, stepId, decision)}
          />
          <div>
            <p className="label mb-2">Activity Log</p>
            <AuditTrail events={auditEvents} loading={auditLoading} clientView />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalApprovalsPage
// ---------------------------------------------------------------------------

export function PortalApprovalsPage() {
  const { user, devHeaders } = usePortalAuth();

  const [approvals, setApprovals]   = useState<Approval[]>([]);
  const [documents, setDocuments]   = useState<DocumentSummary[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await approvalsApi.list(user.projectId, devHeaders) as Approval[];
      setApprovals(data);
      // Titles/links for the documents being approved. Best effort: an approval
      // still works without its document card if this fails.
      documentsApi.list(user.projectId, devHeaders)
        .then((docs) => setDocuments(docs as DocumentSummary[]))
        .catch(() => setDocuments([]));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [user?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleDecide = async (
    approvalId: string,
    stepId: string,
    decision: 'approved' | 'rejected',
  ) => {
    await approvalsApi.decide(approvalId, stepId, { decision }, devHeaders);
    const updated = await approvalsApi.get(approvalId, devHeaders) as Approval;
    setApprovals((prev) => prev.map((a) => a.id === approvalId ? updated : a));
    if (user?.projectId) notifyProjectOverviewChanged(user.projectId);
  };

  // waiting = needs this user now; inProgress = running but not on them;
  // completed = approved / rejected / cancelled. (An 'in_progress' approval used
  // to fall under "Completed" because only 'pending' counted as open.)
  const { waiting, inProgress, completed } = groupApprovalsForUser(approvals, user?.userId ?? '');
  const myPending = waiting;

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="page-title">Approvals</h1>
        <p className="text-sm text-muted mt-1">
          Review and approve items that require your sign-off.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* My actions banner */}
      {!loading && myPending.length > 0 && (
        <div
          className="card p-4 mb-4 flex items-center gap-3"
          style={{ borderColor: 'var(--blue)', borderWidth: '1.5px' }}
        >
          <Shield size={18} className="text-blue shrink-0" />
          <div>
            <p className="text-sm font-semibold text-ink">
              {myPending.length} approval{myPending.length !== 1 ? 's' : ''} waiting for you
            </p>
            <p className="text-xs text-muted mt-0.5">
              Expand the items below to approve or reject.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[1,2,3].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
        </div>
      ) : approvals.length === 0 ? (
        <div className="empty-state py-16">
          <CheckCircle size={32} className="text-muted/30 mb-3 mx-auto" />
          <p className="text-sm text-muted">No approval workflows yet.</p>
          <p className="text-xs text-muted/70 mt-1">
            Your implementation team will notify you when items need your approval.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {waiting.length > 0 && (
            <div>
              <p className="label mb-2">Waiting for you ({waiting.length})</p>
              <div className="space-y-2">
                {waiting.map((a) => (
                  <ApprovalItem
                    key={a.id}
                    approval={a}
                    currentUserId={user?.userId ?? ''}
                    document={findApprovalDocument(a, documents)}
                    onDecide={handleDecide}
                  />
                ))}
              </div>
            </div>
          )}
          {inProgress.length > 0 && (
            <div>
              <p className="label mb-2">In progress ({inProgress.length})</p>
              <div className="space-y-2">
                {inProgress.map((a) => (
                  <ApprovalItem
                    key={a.id}
                    approval={a}
                    currentUserId={user?.userId ?? ''}
                    document={findApprovalDocument(a, documents)}
                    onDecide={handleDecide}
                  />
                ))}
              </div>
            </div>
          )}
          {completed.length > 0 && (
            <div>
              <p className="label mb-2">Completed ({completed.length})</p>
              <div className="space-y-2">
                {completed.map((a) => (
                  <ApprovalItem
                    key={a.id}
                    approval={a}
                    currentUserId={user?.userId ?? ''}
                    document={findApprovalDocument(a, documents)}
                    onDecide={handleDecide}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
